import { ApiError, ErrorInfo, OfficeError } from '../types'

export class AppError extends Error {
  public code: string
  public details?: any
  public timestamp: Date

  constructor(message: string, code: string = 'APP_ERROR', details?: any) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.details = details
    this.timestamp = new Date()
  }
}

export const handleError = (error: unknown): ErrorInfo => {
  if (error instanceof AppError) {
    return {
      message: error.message,
      code: error.code,
      details: error.details,
    }
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'string') {
    return {
      message: error,
    }
  }

  return {
    message: 'An unknown error occurred',
    details: error,
  }
}

export const isApiError = (error: any): error is ApiError => {
  return (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    'code' in error &&
    'timestamp' in error
  )
}

export const isOfficeError = (error: any): error is OfficeError => {
  return (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    'message' in error &&
    'code' in error
  )
}

export const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    return error.message
  }

  if (isOfficeError(error)) {
    return `${error.name}: ${error.message}`
  }

  const errorInfo = handleError(error)
  return errorInfo.message
}

export const createErrorLogger = (context: string) => {
  return {
    log: (message: string, error?: unknown) => {
      console.error(`[${context}] ${message}`, error)
    },
    
    logAndThrow: (message: string, code: string, error?: unknown): never => {
      console.error(`[${context}] ${message}`, error)
      throw new AppError(message, code, error)
    },
    
    logAndReturn: <T>(message: string, defaultValue: T, error?: unknown): T => {
      console.error(`[${context}] ${message}`, error)
      return defaultValue
    },
  }
}

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number
    initialDelay?: number
    maxDelay?: number
    factor?: number
    shouldRetry?: (error: unknown) => boolean
  } = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    factor = 2,
    shouldRetry = () => true,
  } = options

  let delay = initialDelay
  let lastError: unknown

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      
      if (i === maxRetries || !shouldRetry(error)) {
        throw error
      }

      await new Promise(resolve => setTimeout(resolve, delay))
      delay = Math.min(delay * factor, maxDelay)
    }
  }

  throw lastError
}

export const errorBoundary = async <T>(
  fn: () => Promise<T>,
  fallback: T,
  onError?: (error: unknown) => void
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    onError?.(error)
    return fallback
  }
}